import { MaskedInput } from '@/components'
import { Box, Stack, Typography } from '@mui/material'
import { Controller, useFormContext } from 'react-hook-form'
import { FiscalNoteFormData } from '../../schema'
import { useEffect } from 'react'
import { useCompany } from '@/context/companies'

export function Taxes() {
  const { company } = useCompany()
  const { control, watch, setValue } = useFormContext<FiscalNoteFormData>()

  const [value, borrowerTaxIdType] = watch(['value', 'borrower.tax_id_type'])

  const isNormalAssumedProfit = company.tax_regime?.tax_regime.includes(
    'normal_assumed_profit',
  )

  const MIN_VALUE = 10

  useEffect(() => {
    if (!value || !isNormalAssumedProfit || borrowerTaxIdType !== 'cnpj') return

    const numValue = Number(value)

    const irrfValue = numValue * 0.015 // 1.5%
    setValue('irrf', irrfValue < 10 ? '0.00' : irrfValue.toFixed(2).toString())

    // PIS, COFINS, CSLL: zerados quando a soma dos três for menor que R$ 10,00
    const csllValue = numValue * 0.01 // 1%
    const pisValue = numValue * 0.0065 // 0.65%
    const cofinsValue = numValue * 0.03 // 3%
    const sumPisCofinsCsll = csllValue + pisValue + cofinsValue

    const zeroIfSumIsBelowToMinValue = (value: number) =>
      sumPisCofinsCsll < MIN_VALUE ? '0.00' : value.toFixed(2).toString()

    setValue('csll', zeroIfSumIsBelowToMinValue(csllValue))
    setValue('pis', zeroIfSumIsBelowToMinValue(pisValue))
    setValue('cofins', zeroIfSumIsBelowToMinValue(cofinsValue))
  }, [value])

  if (!isNormalAssumedProfit || borrowerTaxIdType !== 'cnpj') {
    return null
  }

  return (
    <Box>
      <Typography variant="h6" mb={2}>
        Impostos
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
        <Controller
          control={control}
          name="csll"
          render={({ field, formState: { errors } }) => (
            <MaskedInput
              label="CSLL"
              format="currency"
              error={!!errors.csll}
              helperText={errors.csll?.message}
              data-testid="csll"
              {...field}
            />
          )}
        />
        <Controller
          control={control}
          name="irrf"
          render={({ field, formState: { errors } }) => (
            <MaskedInput
              label="IRRF"
              format="currency"
              error={!!errors.irrf}
              helperText={errors.irrf?.message}
              data-testid="irrf"
              {...field}
            />
          )}
        />
        <Controller
          control={control}
          name="pis"
          render={({ field, formState: { errors } }) => (
            <MaskedInput
              label="PIS"
              format="currency"
              error={!!errors.pis}
              helperText={errors.pis?.message}
              data-testid="pis"
              {...field}
            />
          )}
        />
        <Controller
          control={control}
          name="cofins"
          render={({ field, formState: { errors } }) => (
            <MaskedInput
              label="COFINS"
              format="currency"
              error={!!errors.cofins}
              helperText={errors.cofins?.message}
              data-testid="cofins"
              {...field}
            />
          )}
        />
      </Stack>
    </Box>
  )
}
